import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';
import materialService from '../../../services/materialService';
import materialCategoryService from '../../../services/materialCategoryService';

const MaterialSelectionModal = ({ isOpen, onClose, onSelectMaterial }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [materials, setMaterials] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen) {
      fetchMaterials();
      fetchCategories();
    }
  }, [isOpen]);

  const fetchMaterials = async () => {
    setLoading(true);
    setError(null);
    const { data, error: fetchError } = await materialService?.getAllMaterials();
    if (fetchError) {
      setError(fetchError);
      setMaterials([]);
    } else {
      setMaterials(data || []);
    }
    setLoading(false);
  };

  const fetchCategories = async () => {
    try {
      const { data: fetchedCategories, error: categoryError } = await materialCategoryService?.getAllCategories();
      if (categoryError) {
        console.error('Error loading categories:', categoryError);
        setCategories([{ value: 'all', label: 'All Categories' }]);
        return;
      }
      const categoryOptions = [
        { value: 'all', label: 'All Categories' },
        ...(fetchedCategories || [])?.map(cat => ({
          value: cat?.id,
          label: cat?.name
        }))
      ];
      setCategories(categoryOptions);
    } catch (err) {
      console.error('Error loading categories:', err);
      setCategories([{ value: 'all', label: 'All Categories' }]);
    }
  };

  const filteredMaterials = materials?.filter(material => {
    const matchesSearch = material?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
                         material?.sku?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
                         material?.material_category?.toLowerCase()?.includes(searchQuery?.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || material?.material_category === selectedCategory;
    
    return matchesSearch && matchesCategory;
  });

  const handleSelectMaterial = (material) => {
    if (onSelectMaterial) {
      onSelectMaterial({
        id: material?.id,
        name: material?.name,
        costPerUnit: material?.cost_per_unit || 0,
        unit: material?.unit_of_measure || 'unit',
        materialAttributes: material?.material_attributes || ''
      });
    }
    onClose();
  };

  const handleReset = () => {
    setSearchQuery('');
    setSelectedCategory('all');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-card rounded-xl shadow-xl w-full max-w-4xl mx-4 max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <h3 className="text-xl font-semibold text-foreground">Material Selection</h3>
          <button
            onClick={onClose}
            className="flex items-center justify-center w-8 h-8 rounded-lg hover:bg-muted transition-colors duration-200"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        <div className="p-6 border-b border-border">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Search</label>
              <Input
                type="search"
                placeholder="Search materials by name, SKU, category..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e?.target?.value)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Category</label>
              <Select
                value={selectedCategory}
                onChange={(value) => setSelectedCategory(value)}
                options={categories}
              />
            </div>
          </div>
          <div className="mt-4 flex justify-end">
            <Button variant="outline" onClick={handleReset}>
              <Icon name="RotateCcw" size={16} className="mr-2" />
              Reset Filters
            </Button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Icon name="Loader" size={48} color="var(--color-muted-foreground)" className="animate-spin" />
              <p className="mt-4 text-sm text-muted-foreground">Loading materials...</p>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Icon name="AlertCircle" size={48} color="var(--color-destructive)" />
              <p className="mt-4 text-sm text-destructive">Error loading materials</p>
              <p className="text-xs text-muted-foreground">{error}</p>
            </div>
          ) : filteredMaterials?.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Icon name="Package" size={48} color="var(--color-muted-foreground)" />
              <p className="mt-4 text-sm text-muted-foreground">No materials found</p>
              <p className="text-xs text-muted-foreground">Try adjusting your filters</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-3">
              {filteredMaterials?.map((material) => (
                <div
                  key={material?.id}
                  className="flex items-start justify-between p-4 border border-border rounded-lg hover:bg-muted/50 transition-colors duration-200 cursor-pointer"
                  onClick={() => handleSelectMaterial(material)}
                >
                  <div className="flex-1">
                    <div className="flex items-start gap-3">
                      <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-primary/10 flex-shrink-0">
                        <Icon name="Package" size={20} color="var(--color-primary)" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="text-sm font-semibold text-foreground">{material?.name}</h4>
                        <p className="text-xs text-muted-foreground">
                          {material?.sku && `SKU: ${material?.sku} • `}
                          {material?.material_category || 'Uncategorized'}
                        </p>
                        {material?.material_attributes && (
                          <div className="mt-2 p-2 bg-muted/50 rounded">
                            <p className="text-xs font-medium text-foreground">Attributes:</p>
                            <p className="text-xs text-muted-foreground">{material?.material_attributes}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 ml-4">
                    <div className="text-right">
                      <p className="text-xs text-muted-foreground">Cost per Unit</p>
                      <p className="text-sm font-semibold text-foreground">
                        ${material?.cost_per_unit?.toFixed(2) || '0.00'}
                      </p>
                      <p className="text-xs text-muted-foreground">per {material?.unit_of_measure || 'unit'}</p>
                    </div>
                    <Button size="sm" onClick={(e) => {
                      e?.stopPropagation();
                      handleSelectMaterial(material);
                    }}>
                      Select
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-border flex justify-end gap-3">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
};

export default MaterialSelectionModal;